import { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  queryClient,
  apiRequest,
  publicBookingRequest,
  fetchAdminCustomerDetailMerged,
  resetAdminBookingsQueries,
  adminBusinessQueryKey,
  fetchAdminBusinessJson,
} from "@/lib/queryClient";
import { useAdminServicesCatalog, useAdminStaffCatalog } from "@/hooks/useAdminPagedCatalogs";
import {
  apiPublicPath,
  resolvePublicSlug,
  slotsToUi,
  localDateTimeToIso,
  unwrapPagedList,
  coerceBookingApiEntityId,
  type BookingApiSlot,
  type UiSlot,
} from "@/lib/bookingApi";
import { useToast } from "@/hooks/useToast";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import { Search, Person, ChevronLeft, CloseOutlined, PersonAddAlt } from "@mui/icons-material";
import type { Theme } from "@mui/material/styles";
import type { Business, Customer } from "@shared/schema";
import {
  normalizeAdminCustomersListPayload,
  type CustomerDetailResponse,
  type NewBookingDrawerPreset,
} from "@/lib/adminApi";
import { validateEmailRequired, validateOptionalPhone, validateRequiredName } from "@/lib/formValidation";
import { formatCents, sameEntityId } from "@/lib/utils";

/**
 * Drawer uses `zIndex.modal + 2`. Select’s Menu is a Popover/Modal at default `zIndex.modal`, so it
 * stacks *under* the whole drawer — the list is open but invisible. Raise the Menu root, not only Paper.
 */
const drawerSelectProps = {
  MenuProps: {
    sx: (theme: Theme) => ({
      zIndex: theme.zIndex.modal + 50,
    }),
    PaperProps: {
      sx: {
        maxHeight: 320,
      },
    },
    anchorOrigin: { vertical: "bottom" as const, horizontal: "left" as const },
    transformOrigin: { vertical: "top" as const, horizontal: "left" as const },
  },
};

interface SelectedCustomer {
  type: "existing";
  customer: Customer;
}
interface NewCustomer {
  type: "new";
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}
type CustomerSelection = SelectedCustomer | NewCustomer | null;

type NewCustomerFieldErrors = Partial<Record<keyof Pick<NewCustomer, "firstName" | "lastName" | "email" | "phone">, string>>;

type StepIndex = 0 | 1 | 2 | 3;

function addDaysIso(days: number): string {
  const x = new Date();
  x.setDate(x.getDate() + days);
  return x.toISOString().slice(0, 10);
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preset?: NewBookingDrawerPreset;
}

export default function NewBookingDrawer({ open, onOpenChange, preset }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [step, setStep] = useState<StepIndex>(0);

  /** Used for `aria-label` only; step circles stay numeric without visible captions. */
  const stepAriaLabels = useMemo(
    () => [
      t("newBookingDrawer.steps.customer"),
      t("newBookingDrawer.steps.serviceStaff"),
      t("newBookingDrawer.steps.dateTime"),
      t("newBookingDrawer.steps.confirm"),
    ],
    [t],
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [customerSelection, setCustomerSelection] = useState<CustomerSelection>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ firstName: "", lastName: "", email: "", phone: "" });
  const [newCustomerFieldErrors, setNewCustomerFieldErrors] = useState<NewCustomerFieldErrors>({});

  const [serviceId, setServiceId] = useState("");
  const [staffId, setStaffId] = useState("any");

  const [date, setDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");

  const skipTimeClearRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    if (!open) {
      setStep(0);
      setSearchQuery("");
      setDebouncedSearch("");
      setCustomerSelection(null);
      setShowNewForm(false);
      setNewCustomer({ firstName: "", lastName: "", email: "", phone: "" });
      setNewCustomerFieldErrors({});
      setServiceId("");
      setStaffId("any");
      setDate("");
      setSelectedTime("");
      skipTimeClearRef.current = false;
      return;
    }
    if (preset) {
      if (preset.serviceId) setServiceId(preset.serviceId);
      if (preset.staffId) setStaffId(preset.staffId);
      if (preset.date) setDate(preset.date);
      if (preset.time) {
        skipTimeClearRef.current = true;
        setSelectedTime(preset.time);
      }
    }
  }, [open, preset]);

  useEffect(() => {
    if (!open) return;
    if (skipTimeClearRef.current) {
      skipTimeClearRef.current = false;
      return;
    }
    setSelectedTime("");
  }, [date, serviceId, staffId]);

  const { data: presetDetail } = useQuery<CustomerDetailResponse>({
    queryKey: ["/api/admin/customers", preset?.customerId, "detail"],
    queryFn: () => fetchAdminCustomerDetailMerged(preset!.customerId!),
    enabled: open && !!preset?.customerId,
  });

  useEffect(() => {
    if (!open || !preset?.customerId || !presetDetail?.customer) return;
    setCustomerSelection({ type: "existing", customer: presetDetail.customer });
    if (preset.editingBookingId && preset.date && preset.time) {
      setStep(3);
    } else {
      setStep(1);
    }
  }, [open, preset?.customerId, presetDetail, preset?.editingBookingId, preset?.date, preset?.time]);

  /** Bookings list API often omits customer id; prefill name/email from the row for PATCH edit. */
  useEffect(() => {
    if (!open || !preset?.editingBookingId || !preset.prefillCustomer) return;
    if (preset.customerId) return;
    const { firstName, lastName, email, phone } = preset.prefillCustomer;
    setCustomerSelection({
      type: "new",
      firstName,
      lastName,
      email,
      phone: phone ?? "",
    });
    if (preset.date && preset.time) setStep(3);
  }, [open, preset?.editingBookingId, preset?.customerId, preset?.prefillCustomer, preset?.date, preset?.time]);

  const { data: customerResults, isFetching: searchingCustomers } = useQuery({
    queryKey: ["/api/admin/customers-search", debouncedSearch],
    queryFn: async () => {
      const path = `/api/admin/customers?q=${encodeURIComponent(debouncedSearch)}&limit=8`;
      const res = await apiRequest("GET", path);
      return normalizeAdminCustomersListPayload(await res.json());
    },
    enabled: open && debouncedSearch.length >= 2,
  });

  const { services, isLoading: loadingServices } = useAdminServicesCatalog({ enabled: open });
  const { staff: staffList } = useAdminStaffCatalog({ enabled: open });

  const { data: adminBusiness } = useQuery<Pick<Business, "slug" | "currency"> | null>({
    queryKey: adminBusinessQueryKey,
    queryFn: fetchAdminBusinessJson,
    enabled: open,
  });

  const selectedService = services?.find((s) => sameEntityId(s.id, serviceId));

  const serviceChangedFromSnapshot = useMemo(
    () =>
      Boolean(preset?.editingBookingId) &&
      preset?.serviceId != null &&
      preset.serviceId !== "" &&
      serviceId !== "" &&
      !sameEntityId(serviceId, preset.serviceId),
    [preset?.editingBookingId, preset?.serviceId, serviceId],
  );

  /** Matches booking card/detail when editing; use catalog if user picks a different offering. */
  const displayOfferingName =
    preset?.editingBookingId && !serviceChangedFromSnapshot && preset.reviewOfferingName
      ? preset.reviewOfferingName
      : selectedService?.name ?? "";

  const displayPriceCents = useMemo(() => {
    if (preset?.editingBookingId && !serviceChangedFromSnapshot && preset.reviewPriceCents != null) {
      return preset.reviewPriceCents;
    }
    return selectedService?.priceCents ?? 0;
  }, [preset?.editingBookingId, preset?.reviewPriceCents, serviceChangedFromSnapshot, selectedService?.priceCents]);

  const displayCurrency = useMemo(
    () => preset?.reviewCurrency ?? adminBusiness?.currency ?? selectedService?.currency ?? "GBP",
    [preset?.reviewCurrency, adminBusiness?.currency, selectedService?.currency],
  );
  const publicSlug = useMemo(() => {
    const fromSettings = adminBusiness?.slug?.trim();
    if (fromSettings) return fromSettings;
    return resolvePublicSlug();
  }, [adminBusiness?.slug]);

  const { data: slots, isFetching: loadingSlots } = useQuery<UiSlot[]>({
    queryKey: ["public-availability", publicSlug, serviceId, staffId, date],
    queryFn: async () => {
      const params = new URLSearchParams({ serviceId, date });
      if (staffId !== "any") params.set("staffId", staffId);
      const res = await publicBookingRequest("GET", `${apiPublicPath(publicSlug!, "/availability")}?${params}`);
      const raw = unwrapPagedList<BookingApiSlot>(await res.json());
      return slotsToUi(raw);
    },
    enabled: open && !!publicSlug && !!serviceId && !!date,
  });

  const activeServices = services?.filter((s) => s.active) ?? [];
  const activeStaff = staffList?.filter((s) => s.active) ?? [];

  const saveMutation = useMutation({
    mutationFn: (payload: { body: Record<string, unknown>; editingId?: string }) =>
      payload.editingId
        ? apiRequest("PATCH", `/api/admin/bookings/${payload.editingId}`, payload.body)
        : apiRequest("POST", "/api/admin/bookings", payload.body),
    onSuccess: async (_data, variables) => {
      await resetAdminBookingsQueries(queryClient);
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/customers"] });
      if (variables.editingId) {
        toast({ title: t("toasts.bookingUpdated") });
      } else {
        toast({ title: t("toasts.bookingCreated"), description: t("toasts.bookingCreatedDescription") });
      }
      onOpenChange(false);
    },
    onError: (e: Error) => {
      toast({ title: t("toasts.createBookingFailed"), description: e.message, variant: "destructive" });
    },
  });

  function handleSubmit() {
    const customer =
      customerSelection?.type === "existing"
        ? {
            firstName: customerSelection.customer.firstName,
            lastName: customerSelection.customer.lastName,
            email: customerSelection.customer.email,
            phone: customerSelection.customer.phone ?? "",
          }
        : customerSelection?.type === "new"
          ? {
              firstName: customerSelection.firstName,
              lastName: customerSelection.lastName,
              email: customerSelection.email,
              phone: customerSelection.phone,
            }
          : {
              firstName: newCustomer.firstName,
              lastName: newCustomer.lastName,
              email: newCustomer.email,
              phone: newCustomer.phone,
            };

    const slot = slots?.find((s) => s.time === selectedTime);
    const staffIdNum =
      staffId === "any"
        ? slot?.staffId
        : staffId
          ? coerceBookingApiEntityId(staffId)
          : undefined;
    if (staffIdNum == null) {
      toast({
        title: t("toasts.createBookingFailed"),
        description: t("newBookingDrawer.missingStaffForSlot"),
        variant: "destructive",
      });
      return;
    }
    const body = {
      customerEmail: customer.email,
      customerName: `${customer.firstName} ${customer.lastName}`.trim(),
      customerPhone: customer.phone || undefined,
      serviceId: coerceBookingApiEntityId(serviceId),
      staffId: staffIdNum,
      startAt: localDateTimeToIso(date, selectedTime),
    };
    saveMutation.mutate({
      body,
      editingId: preset?.editingBookingId,
    });
  }

  const step0Valid =
    customerSelection !== null || (showNewForm && !!newCustomer.firstName && !!newCustomer.lastName && !!newCustomer.email);
  const step1Valid = !!serviceId;
  const step2Valid = !!date && !!selectedTime;

  function handleNext() {
    if (step === 0 && showNewForm && !customerSelection) {
      const next: NewCustomerFieldErrors = {};
      const fn = validateRequiredName(newCustomer.firstName, t("newBookingDrawer.firstName"));
      if (fn) next.firstName = fn;
      const ln = validateRequiredName(newCustomer.lastName, t("newBookingDrawer.lastName"));
      if (ln) next.lastName = ln;
      const em = validateEmailRequired(newCustomer.email);
      if (em) next.email = em;
      const ph = validateOptionalPhone(newCustomer.phone);
      if (ph) next.phone = ph;
      setNewCustomerFieldErrors(next);
      if (Object.keys(next).length > 0) return;
      setCustomerSelection({ type: "new", ...newCustomer });
    }
    setStep((s) => Math.min(s + 1, 3) as StepIndex);
  }

  function canGoToStep(target: StepIndex): boolean {
    if (target <= step) return true;
    if (target >= 1 && !step0Valid) return false;
    if (target >= 2 && !step1Valid) return false;
    if (target >= 3 && !step2Valid) return false;
    return true;
  }

  function getCustomerLabel(): string {
    if (!customerSelection) return "";
    if (customerSelection.type === "existing") {
      return `${customerSelection.customer.firstName} ${customerSelection.customer.lastName}`;
    }
    return `${customerSelection.firstName} ${customerSelection.lastName}`;
  }

  function getCustomerEmail(): string {
    if (!customerSelection) return "";
    if (customerSelection.type === "existing") return customerSelection.customer.email;
    return customerSelection.email;
  }

  const todayStr = new Date().toISOString().slice(0, 10);
  const maxStr = addDaysIso(365);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => onOpenChange(false)}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      PaperProps={{ sx: { width: { xs: "100%", sm: 420 }, p: 2 } }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">
          {preset?.editingBookingId ? t("newBookingDrawer.editTitle") : t("newBookingDrawer.title")}
        </Typography>
        <IconButton onClick={() => onOpenChange(false)} aria-label={t("newBookingDrawer.close")}>
          <CloseOutlined />
        </IconButton>
      </Box>

      <Stepper nonLinear activeStep={step} sx={{ mb: 3 }}>
        {([0, 1, 2, 3] as const).map((index) => (
          <Step key={index} completed={index < step}>
            <StepButton
              onClick={() => {
                const target = index as StepIndex;
                if (canGoToStep(target)) setStep(target);
              }}
              disabled={index > step && !canGoToStep(index as StepIndex)}
              aria-label={stepAriaLabels[index]}
              sx={{
                "& .MuiStepLabel-label": { display: "none" },
              }}
              data-testid={`step-booking-${index}`}
            />
          </Step>
        ))}
      </Stepper>

      <Box sx={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
        {step === 0 && (
          <>
            <Typography variant="overline" color="text.secondary">
              {t("newBookingDrawer.steps.customer")}
            </Typography>
            {customerSelection ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
                <Person color="primary" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={600} noWrap>
                    {getCustomerLabel()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {getCustomerEmail()}
                  </Typography>
                  {customerSelection.type === "new" && (
                    <Chip size="small" label={t("newBookingDrawer.newCustomerChip")} variant="outlined" sx={{ mt: 0.5 }} />
                  )}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => {
                    setCustomerSelection(null);
                    setShowNewForm(false);
                    setNewCustomerFieldErrors({});
                  }}
                  data-testid="button-clear-customer"
                >
                  <CloseOutlined fontSize="small" />
                </IconButton>
              </Box>
            ) : showNewForm ? (
              <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                  <Typography fontWeight={600}>{t("newBookingDrawer.newCustomerDetails")}</Typography>
                  <Button size="small" onClick={() => setShowNewForm(false)}>
                    {t("common.cancel")}
                  </Button>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  <TextField
                    required
                    size="small"
                    label={t("newBookingDrawer.firstName")}
                    value={newCustomer.firstName}
                    onChange={(e) => {
                      setNewCustomer((n) => ({ ...n, firstName: e.target.value }));
                      if (newCustomerFieldErrors.firstName) setNewCustomerFieldErrors((prev) => ({ ...prev, firstName: undefined }));
                    }}
                    error={Boolean(newCustomerFieldErrors.firstName)}
                    helperText={newCustomerFieldErrors.firstName}
                    data-testid="input-new-first-name"
                  />
                  <TextField
                    required
                    size="small"
                    label={t("newBookingDrawer.lastName")}
                    value={newCustomer.lastName}
                    onChange={(e) => {
                      setNewCustomer((n) => ({ ...n, lastName: e.target.value }));
                      if (newCustomerFieldErrors.lastName) setNewCustomerFieldErrors((prev) => ({ ...prev, lastName: undefined }));
                    }}
                    error={Boolean(newCustomerFieldErrors.lastName)}
                    helperText={newCustomerFieldErrors.lastName}
                    data-testid="input-new-last-name"
                  />
                </Box>
                <TextField
                  required
                  size="small"
                  label={t("newBookingDrawer.email")}
                  type="email"
                  fullWidth
                  sx={{ mt: 2 }}
                  value={newCustomer.email}
                  onChange={(e) => {
                    setNewCustomer((n) => ({ ...n, email: e.target.value }));
                    if (newCustomerFieldErrors.email) setNewCustomerFieldErrors((prev) => ({ ...prev, email: undefined }));
                  }}
                  error={Boolean(newCustomerFieldErrors.email)}
                  helperText={newCustomerFieldErrors.email}
                  data-testid="input-new-email"
                />
                <TextField
                  size="small"
                  label={t("newBookingDrawer.phoneOptional")}
                  fullWidth
                  sx={{ mt: 2 }}
                  value={newCustomer.phone}
                  onChange={(e) => {
                    setNewCustomer((n) => ({ ...n, phone: e.target.value }));
                    if (newCustomerFieldErrors.phone) setNewCustomerFieldErrors((prev) => ({ ...prev, phone: undefined }));
                  }}
                  error={Boolean(newCustomerFieldErrors.phone)}
                  helperText={newCustomerFieldErrors.phone}
                  data-testid="input-new-phone"
                />
              </Box>
            ) : (
              <>
                <TextField
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: "action.active" }} fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  data-testid="input-customer-search"
                />
                {debouncedSearch.length >= 2 && (
                  <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, maxHeight: 208, overflow: "auto" }}>
                    {searchingCustomers ? (
                      <Box sx={{ p: 2 }}>
                        <Skeleton height={40} />
                        <Skeleton height={40} />
                      </Box>
                    ) : !customerResults?.length ? (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
                        {t("newBookingDrawer.noCustomersFound")}
                      </Typography>
                    ) : (
                      customerResults.map((c) => (
                        <Button
                          key={c.id}
                          fullWidth
                          sx={{ justifyContent: "flex-start", textTransform: "none", py: 1.5, px: 2 }}
                          onClick={() => {
                            setCustomerSelection({ type: "existing", customer: c });
                            setSearchQuery("");
                          }}
                          data-testid={`button-select-customer-${c.id}`}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              bgcolor: "action.hover",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              typography: "caption",
                              mr: 1.5,
                            }}
                          >
                            {c.firstName?.[0] ?? ""}
                            {c.lastName?.[0] ?? ""}
                          </Box>
                          <Box sx={{ textAlign: "left", minWidth: 0 }}>
                            <Typography fontWeight={600} noWrap>
                              {c.firstName} {c.lastName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {c.email}
                            </Typography>
                          </Box>
                        </Button>
                      ))
                    )}
                  </Box>
                )}
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<PersonAddAlt />}
                  onClick={() => {
                    setShowNewForm(true);
                    setNewCustomerFieldErrors({});
                  }}
                  data-testid="button-new-customer"
                >
                  {t("newBookingDrawer.addNewCustomer")}
                </Button>
              </>
            )}
          </>
        )}

        {step === 1 && (
          <>
            <Typography variant="overline" color="text.secondary">
              {t("newBookingDrawer.offeringStaff")}
            </Typography>
            <TextField
              required
              select
              label={t("newBookingDrawer.offering")}
              fullWidth
              value={serviceId}
              onChange={(e) => {
                setServiceId(e.target.value);
                setStaffId("any");
              }}
              data-testid="select-service"
              SelectProps={drawerSelectProps}
            >
              <MenuItem value="">
                <em>{t("newBookingDrawer.chooseOffering")}</em>
              </MenuItem>
              {activeServices.map((s) => (
                <MenuItem key={s.id} value={s.id} data-testid="trigger-select-service">
                  {s.name} ({t("newBookingDrawer.durationMin", { mins: s.durationMins })})
                </MenuItem>
              ))}
            </TextField>
            {loadingServices && (
              <Typography variant="caption" color="text.secondary">
                {t("common.loading")}
              </Typography>
            )}
            {!loadingServices && activeServices.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                {t("newBookingDrawer.noOfferingsAvailable")}
              </Typography>
            )}
            {!!serviceId && (
              <TextField
                select
                label={t("newBookingDrawer.teamMember")}
                fullWidth
                value={staffId}
                onChange={(e) => setStaffId(e.target.value)}
                data-testid="select-staff"
                SelectProps={drawerSelectProps}
              >
                <MenuItem value="any">{t("bookingFlow.anyStaff")}</MenuItem>
                {activeStaff.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.displayName}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {selectedService && (
              <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
                <Typography fontWeight={600}>{displayOfferingName || selectedService.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t("newBookingDrawer.durationMin", { mins: selectedService.durationMins })}
                  {displayPriceCents > 0 ? ` · ${formatCents(displayPriceCents, displayCurrency)}` : ""}
                </Typography>
              </Box>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <Typography variant="overline" color="text.secondary">
              {t("newBookingDrawer.dateTimeSection")}
            </Typography>
            <TextField
              required
              type="date"
              label={t("newBookingDrawer.date")}
              fullWidth
              value={date}
              onChange={(e) => setDate(e.target.value)}
              inputProps={{ min: todayStr, max: maxStr }}
              InputLabelProps={{ shrink: true }}
              data-testid="input-booking-date"
            />
            {date && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                  {t("newBookingDrawer.availableSlots")}
                </Typography>
                {loadingSlots ? (
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <Skeleton key={i} height={36} />
                    ))}
                  </Box>
                ) : !slots?.length ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center", border: 1, borderColor: "divider", borderRadius: 1 }}>
                    No available slots on this date
                  </Typography>
                ) : (
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1 }}>
                    {slots.map((slot) => (
                      <Button
                        key={slot.time}
                        size="small"
                        variant={selectedTime === slot.time ? "contained" : "outlined"}
                        onClick={() => setSelectedTime(slot.time)}
                        data-testid={`button-slot-${slot.time}`}
                      >
                        {slot.display}
                      </Button>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </>
        )}

        {step === 3 && (
          <>
            <Typography variant="overline" color="text.secondary">
              {t("newBookingDrawer.reviewConfirm")}
            </Typography>
            <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
              <Box sx={{ p: 2, display: "flex", gap: 2, borderBottom: 1, borderColor: "divider" }}>
                <Typography color="text.secondary" sx={{ width: 100, flexShrink: 0 }}>
                  {t("newBookingDrawer.reviewCustomer")}
                </Typography>
                <Box>
                  <Typography fontWeight={600}>{getCustomerLabel()}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {getCustomerEmail()}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ p: 2, display: "flex", gap: 2, borderBottom: 1, borderColor: "divider" }}>
                <Typography color="text.secondary" sx={{ width: 100, flexShrink: 0 }}>
                  {t("newBookingDrawer.reviewOffering")}
                </Typography>
                <Box>
                  <Typography fontWeight={600}>{displayOfferingName || selectedService?.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedService?.durationMins} min
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ p: 2, display: "flex", gap: 2, borderBottom: 1, borderColor: "divider" }}>
                <Typography color="text.secondary" sx={{ width: 100, flexShrink: 0 }}>
                  {t("newBookingDrawer.reviewStaff")}
                </Typography>
                <Typography fontWeight={600}>
                  {staffId === "any"
                    ? t("bookingFlow.anyStaff")
                    : activeStaff.find((s) => sameEntityId(s.id, staffId))?.displayName || t("common.emDash")}
                </Typography>
              </Box>
              <Box sx={{ p: 2, display: "flex", gap: 2, borderBottom: 1, borderColor: "divider" }}>
                <Typography color="text.secondary" sx={{ width: 100, flexShrink: 0 }}>
                  {t("newBookingDrawer.reviewDateTime")}
                </Typography>
                <Box>
                  <Typography fontWeight={600}>
                    {date
                      ? new Date(`${date  }T12:00:00`).toLocaleDateString("en-GB", {
                          weekday: "short",
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : ""}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {slots?.find((s) => s.time === selectedTime)?.display || selectedTime}
                  </Typography>
                </Box>
              </Box>
              {selectedService && displayPriceCents > 0 && (
                <Box sx={{ p: 2, display: "flex", gap: 2, borderBottom: 0 }}>
                  <Typography color="text.secondary" sx={{ width: 100, flexShrink: 0 }}>
                    {t("newBookingDrawer.reviewPrice")}
                  </Typography>
                  <Typography fontWeight={600}>{formatCents(displayPriceCents, displayCurrency)}</Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ p: 2, bgcolor: "warning.50", border: 1, borderColor: "warning.light", borderRadius: 1, typography: "body2" }}>
              {t("newBookingDrawer.immediateConfirm")}
            </Box>
          </>
        )}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: "auto", pt: 2, borderTop: 1, borderColor: "divider" }}>
        {step > 0 ? (
          <Button variant="outlined" startIcon={<ChevronLeft />} onClick={() => setStep((s) => Math.max(s - 1, 0) as StepIndex)} data-testid="button-booking-back">
            {t("common.back")}
          </Button>
        ) : (
          <Button variant="outlined" onClick={() => onOpenChange(false)} data-testid="button-booking-cancel">
            {t("common.cancel")}
          </Button>
        )}
        {step < 3 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={(step === 0 && !step0Valid) || (step === 1 && !step1Valid) || (step === 2 && !step2Valid)}
            data-testid="button-booking-next"
          >
            {t("common.next")}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saveMutation.isPending}
            data-testid="button-booking-submit"
            startIcon={saveMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {saveMutation.isPending
              ? t("newBookingDrawer.booking")
              : preset?.editingBookingId
                ? t("bookingDetailModal.saveChanges")
                : t("newBookingDrawer.confirmBooking")}
          </Button>
        )}
      </Box>
    </Drawer>
  );
}
